import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { orderId, sspSubmitOrder, toggleSspModels } from 'pages/provisioning/aws/sns/store';
import logger from 'utils/logger';
import { resetOrder, resetToggleSspModal, formOrderObjFromCart, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/sns/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/aws/sns/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/sns/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/sns/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/sns/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/sns/ShowCostDetails'));

const AWSSNSProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    // Helper function to fetch landing zone details
    const fetchLandingZoneDetails = async (orderData) => {
        const isProduction = orderData?.environment === 'Production';
        if (
            orderData?.appProfile &&
            orderData?.sector &&
            orderData?.department &&
            orderData?.appRegion &&
            orderData?.environment &&
            (!isProduction || ['yes', 'no'].includes(orderData?.isHighAvailability))
        ) {
            const payload = {
                applicationServiceName: orderData.appProfile,
                sector: orderData.sector,
                environment: orderData.environment,
                department: orderData.department,
                ...(isProduction && { highAvailability: orderData?.isHighAvailability === 'yes' }),
                applicationRegion: orderData.appRegion,
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.Ec2.awsLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                logger.error('Landing zone API error:', error);
                return null;
            }
        }
        return null;
    };
    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, data?.images?.sku);
            const landingZoneDetails = await fetchLandingZoneDetails(orderObj);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                emptyCost: costs,
                landingZoneDetails,
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails, data?.images?.sku);
            const landingZoneDetails = await fetchLandingZoneDetails(orderObj);
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                landingZoneDetails,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>AWS SNS Provisioning</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(AWSSNSProvisioningPage);
